import { getApiErrorMessage } from 'app/utils/apiError';

export const DEFAULT_SHIPMENT_SUCCESS_MESSAGE = 'Shipment has been created successfully';

export const SHIPMENT_CREATE_FALLBACK_ERROR = 'Shipment could not be created. Please try again.';

export const SHIPMENT_CREATE_IN_PROGRESS =
  'A shipment is already being created. Please wait for it to finish.';

export function getShipmentSuccessMessage(dialogParam) {
  return dialogParam?.successMessage ? dialogParam.successMessage : DEFAULT_SHIPMENT_SUCCESS_MESSAGE;
}

/**
 * The thunk rejects with a plain string (`rejectWithValue(getThunkRejectMessage(...))`), which is
 * what `unwrap()` throws. Fall back to reading an error object so a failure raised before that
 * catch — a bad state read, a serialized error if the thunk is bypassed — still says something.
 */
export function getShipmentCreateErrorMessage(error) {
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }

  return getApiErrorMessage(error, SHIPMENT_CREATE_FALLBACK_ERROR);
}

/**
 * PGSS-5165: report what actually happened to the create-shipment request.
 *
 * The submit handler used to be `dispatch(createShipment(data)).then(showSuccess)`. An RTK
 * `createAsyncThunk` promise *always fulfils* — a thrown payload creator resolves with a
 * `rejected` action rather than rejecting — so `.then` fired whether or not the shipment was
 * created, and the user was told "Shipment has been created successfully" every time. That is
 * how the backend duplicate-shipment guard shipped looking broken: it correctly refused to put
 * an already-shipped order on a second shipment, and the UI reported success for the shipment
 * it had just declined to create.
 *
 * `unwrap()` is what turns a rejected action back into a rejected promise, so success and
 * failure land in different branches.
 *
 * @param {{unwrap: function}} dispatched the return value of `dispatch(createShipment(...))`
 * @param {{onSuccess: function, onError: function(string)}} handlers
 * @returns {Promise} resolves once the outcome has been reported; never rejects
 */
export function reportShipmentCreateResult(dispatched, { onSuccess, onError }) {
  return dispatched
    .unwrap()
    .then(() => {
      onSuccess();
    })
    .catch((error) => {
      onError(getShipmentCreateErrorMessage(error));
    });
}

/**
 * PGSS-5572: let a create-shipment submit run once at a time.
 *
 * Creating a shipment takes seconds (up to about a minute for many orders) and nothing on screen
 * changes until it finishes. The side-panel submit disabled the button and re-enabled it on the
 * very next line, without waiting, so the button stayed live for the whole request. A second
 * click sent a second request, and the backend guard could not refuse it: it only sees orders
 * the first request has already linked, which happens near the end. Production had 14 duplicate
 * shipments in 9 days from this.
 *
 * `inFlight` is a ref, not state, so two clicks inside the same render are still caught.
 *
 * @param {{current: boolean}} inFlight ref shared by every submit of the same form
 * @param {function(): Promise} run the submit; its promise must settle when the request does
 * @param {function(boolean)} [setBusy] told when a submit starts and ends, to disable the button
 * @returns {Promise<boolean>} false when dropped because a submit was already running
 */
export function submitOnce(inFlight, run, setBusy) {
  if (inFlight.current) {
    return Promise.resolve(false);
  }
  inFlight.current = true;
  setBusy?.(true);

  return Promise.resolve()
    .then(run)
    .finally(() => {
      inFlight.current = false;
      setBusy?.(false);
    })
    .then(() => true);
}
